export type PreviewVoxelManager = {
  boundsIJK?: number[][];
  modifiedSlices?: Set<number>;
  scalarData?: ArrayLike<number> | null;
  getAtIndex?: (index: number) => unknown;
  setAtIndex?: (index: number, value: number) => unknown;
  getAtIJK?: (i: number, j: number, k: number) => unknown;
  getAtIJKPoint?: (point: [number, number, number]) => unknown;
  setAtIJK?: (i: number, j: number, k: number, value: number) => unknown;
  setAtIJKPoint?: (
    point: [number, number, number],
    value: number,
  ) => unknown;
};

export type VolumeDimensions = [number, number, number];

export type PreviewBitApplyResult = {
  indices: Uint32Array;
  changed: boolean;
};

export type BrushFootprintLinearIndicesParams = {
  dimensions: readonly number[] | undefined;
  origin: readonly number[] | undefined;
  spacing: readonly number[] | undefined;
  direction: readonly number[] | undefined;
  centerWorld: readonly number[] | undefined;
  normalWorld: readonly number[] | undefined;
  uWorld: readonly number[] | undefined;
  vWorld: readonly number[] | undefined;
  radiusMM: number;
};

type Vector3 = readonly [number, number, number];
type Matrix3 = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

function normalizeValue(value: unknown): number {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function getPreviewValue(value: number, enabled: boolean): number {
  return enabled ? value | 0b10 : value & 0b1;
}

function isValidDimensions(
  dimensions: readonly number[] | undefined,
): dimensions is VolumeDimensions {
  return (
    Array.isArray(dimensions) &&
    dimensions.length >= 3 &&
    dimensions.every((dimension) => Number.isFinite(dimension) && dimension > 0)
  );
}

function linearIndexToIJK(
  index: number,
  dimensions: VolumeDimensions,
): [number, number, number] {
  const [dimI, dimJ] = dimensions;
  const frameSize = dimI * dimJ;
  const k = Math.floor(index / frameSize);
  const remainder = index - k * frameSize;
  const j = Math.floor(remainder / dimI);
  const i = remainder - j * dimI;

  return [i, j, k];
}

function getVoxelCount(dimensions: VolumeDimensions): number {
  return dimensions[0] * dimensions[1] * dimensions[2];
}

function isValidLinearIndex(
  index: number,
  voxelCount: number,
): boolean {
  return Number.isInteger(index) && index >= 0 && index < voxelCount;
}

function updateDirtyTracking(
  voxelManager: PreviewVoxelManager,
  point: [number, number, number],
) {
  voxelManager.modifiedSlices?.add(point[2]);

  const bounds = voxelManager.boundsIJK;
  if (!bounds || bounds.length < 3) return;

  for (let axis = 0; axis < 3; axis += 1) {
    if (!bounds[axis]) {
      bounds[axis] = [Infinity, -Infinity];
    }

    bounds[axis][0] = Math.min(bounds[axis][0], point[axis]);
    bounds[axis][1] = Math.max(bounds[axis][1], point[axis]);
  }
}

function getDenseScalarData(
  voxelManager: PreviewVoxelManager,
  voxelCount: number,
): ArrayLike<number> | null {
  const scalarData = voxelManager.scalarData;

  if (!scalarData || scalarData.length < voxelCount) {
    return null;
  }

  return scalarData;
}

function isValidVector3(
  values: readonly number[] | undefined,
): values is Vector3 {
  return (
    Array.isArray(values) &&
    values.length >= 3 &&
    Number.isFinite(values[0]) &&
    Number.isFinite(values[1]) &&
    Number.isFinite(values[2])
  );
}

function isValidDirection(
  values: readonly number[] | undefined,
): values is Matrix3 {
  return (
    Array.isArray(values) &&
    values.length >= 9 &&
    values.slice(0, 9).every((value) => Number.isFinite(value))
  );
}

function dot3(a: readonly number[], b: readonly number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function subWorld(
  a: readonly number[],
  b: readonly number[],
): [number, number, number] {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function worldToIJK(
  origin: readonly number[],
  spacing: readonly number[],
  direction: readonly number[],
  world: readonly number[],
): [number, number, number] {
  const wx = world[0] - origin[0];
  const wy = world[1] - origin[1];
  const wz = world[2] - origin[2];

  return [
    (direction[0] * wx + direction[3] * wy + direction[6] * wz) / spacing[0],
    (direction[1] * wx + direction[4] * wy + direction[7] * wz) / spacing[1],
    (direction[2] * wx + direction[5] * wy + direction[8] * wz) / spacing[2],
  ];
}

function ijkToWorld(
  origin: readonly number[],
  spacing: readonly number[],
  direction: readonly number[],
  i: number,
  j: number,
  k: number,
): [number, number, number] {
  return [
    origin[0] +
      (direction[0] * i + direction[1] * j + direction[2] * k) * spacing[0],
    origin[1] +
      (direction[3] * i + direction[4] * j + direction[5] * k) * spacing[1],
    origin[2] +
      (direction[6] * i + direction[7] * j + direction[8] * k) * spacing[2],
  ];
}

function getSliceHalfThickness(
  spacing: readonly number[],
  direction: readonly number[],
  normalWorld: readonly number[],
): number {
  const vx = [
    direction[0] * spacing[0],
    direction[3] * spacing[1],
    direction[6] * spacing[2],
  ];
  const vy = [
    direction[1] * spacing[0],
    direction[4] * spacing[1],
    direction[7] * spacing[2],
  ];
  const vz = [
    direction[2] * spacing[0],
    direction[5] * spacing[1],
    direction[8] * spacing[2],
  ];

  return (
    0.5 *
    (Math.abs(dot3(vx, normalWorld)) +
      Math.abs(dot3(vy, normalWorld)) +
      Math.abs(dot3(vz, normalWorld)))
  );
}

export function setPreviewBitAtLinearIndex(
  voxelManager: PreviewVoxelManager | null | undefined,
  index: number,
  dimensions: readonly number[] | undefined,
  enabled: boolean,
): boolean {
  if (!voxelManager || !isValidDimensions(dimensions)) {
    return false;
  }

  const [dimI, dimJ, dimK] = dimensions;
  const voxelCount = getVoxelCount([dimI, dimJ, dimK]);
  const linearIndex = Math.trunc(index);

  if (!isValidLinearIndex(linearIndex, voxelCount) || linearIndex !== index) {
    return false;
  }

  if (
    typeof voxelManager.getAtIndex === 'function' &&
    typeof voxelManager.setAtIndex === 'function'
  ) {
    const value = normalizeValue(voxelManager.getAtIndex(linearIndex));
    voxelManager.setAtIndex(linearIndex, getPreviewValue(value, enabled));
    return true;
  }

  const point = linearIndexToIJK(linearIndex, dimensions);
  const value =
    typeof voxelManager.getAtIJK === 'function'
      ? normalizeValue(voxelManager.getAtIJK(point[0], point[1], point[2]))
      : normalizeValue(voxelManager.getAtIJKPoint?.(point));
  const previewValue = getPreviewValue(value, enabled);

  if (typeof voxelManager.setAtIJKPoint === 'function') {
    voxelManager.setAtIJKPoint(point, previewValue);
    return true;
  }

  if (typeof voxelManager.setAtIJK === 'function') {
    voxelManager.setAtIJK(point[0], point[1], point[2], previewValue);
    return true;
  }

  return false;
}

export function applyPreviewBitToLinearIndices(
  voxelManager: PreviewVoxelManager | null | undefined,
  indices: Uint32Array,
  dimensions: readonly number[] | undefined,
  enabled: boolean,
): PreviewBitApplyResult {
  if (!voxelManager || !indices.length || !isValidDimensions(dimensions)) {
    return { indices: new Uint32Array(), changed: false };
  }

  const voxelCount = getVoxelCount(dimensions);
  const scalarData = getDenseScalarData(voxelManager, voxelCount);
  const validIndices: number[] = [];
  let changed = false;

  if (scalarData) {
    const writableScalarData = scalarData as ArrayLike<number> & {
      [index: number]: number;
    };

    for (let idx = 0; idx < indices.length; idx += 1) {
      const linearIndex = indices[idx];
      if (!isValidLinearIndex(linearIndex, voxelCount)) continue;

      validIndices.push(linearIndex);
      const value = normalizeValue(writableScalarData[linearIndex]);
      const nextValue = getPreviewValue(value, enabled);
      if (value === nextValue) continue;

      writableScalarData[linearIndex] = nextValue;
      updateDirtyTracking(
        voxelManager,
        linearIndexToIJK(linearIndex, dimensions),
      );
      changed = true;
    }

    return {
      indices: Uint32Array.from(validIndices),
      changed,
    };
  }

  for (let idx = 0; idx < indices.length; idx += 1) {
    const linearIndex = indices[idx];
    if (!isValidLinearIndex(linearIndex, voxelCount)) continue;

    validIndices.push(linearIndex);
    changed =
      setPreviewBitAtLinearIndex(
        voxelManager,
        linearIndex,
        dimensions,
        enabled,
      ) || changed;
  }

  return {
    indices: Uint32Array.from(validIndices),
    changed,
  };
}

export function getPreviewCommitIndices(
  previewIndices: Uint32Array | null | undefined,
  candidateIndices?: Iterable<number> | null,
): number[] {
  if (!previewIndices?.length) {
    return [];
  }

  if (!candidateIndices) {
    return Array.from(previewIndices);
  }

  const committed = new Set<number>();
  const result: number[] = [];
  const hasPreviewIndex = (target: number): boolean => {
    let low = 0;
    let high = previewIndices.length - 1;

    while (low <= high) {
      const middle = (low + high) >>> 1;
      const value = previewIndices[middle];

      if (value === target) return true;
      if (value < target) {
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    return false;
  };

  for (const candidateIndex of candidateIndices) {
    const numericIndex = Number(candidateIndex);
    const linearIndex = Math.trunc(numericIndex);

    if (
      !Number.isFinite(numericIndex) ||
      linearIndex !== numericIndex ||
      !hasPreviewIndex(linearIndex) ||
      committed.has(linearIndex)
    ) {
      continue;
    }

    committed.add(linearIndex);
    result.push(linearIndex);
  }

  return result;
}

export function getBrushFootprintLinearIndices(
  params: BrushFootprintLinearIndicesParams,
): number[] {
  const {
    dimensions,
    origin,
    spacing,
    direction,
    centerWorld,
    normalWorld,
    uWorld,
    vWorld,
    radiusMM,
  } = params;

  if (
    !isValidDimensions(dimensions) ||
    !isValidVector3(origin) ||
    !isValidVector3(spacing) ||
    !isValidDirection(direction) ||
    !isValidVector3(centerWorld) ||
    !isValidVector3(normalWorld) ||
    !isValidVector3(uWorld) ||
    !isValidVector3(vWorld) ||
    !Number.isFinite(radiusMM) ||
    radiusMM <= 0 ||
    spacing[0] <= 0 ||
    spacing[1] <= 0 ||
    spacing[2] <= 0
  ) {
    return [];
  }

  const [dimI, dimJ, dimK] = dimensions;
  const centerIJK = worldToIJK(origin, spacing, direction, centerWorld);
  const radiusVoxels = radiusMM / spacing[0];
  const radiusSquared = radiusMM * radiusMM;
  const sliceHalfThickness = getSliceHalfThickness(
    spacing,
    direction,
    normalWorld,
  );

  const i0 = Math.max(0, Math.floor(centerIJK[0] - radiusVoxels - 1));
  const i1 = Math.min(dimI - 1, Math.ceil(centerIJK[0] + radiusVoxels + 1));
  const j0 = Math.max(0, Math.floor(centerIJK[1] - radiusVoxels - 1));
  const j1 = Math.min(dimJ - 1, Math.ceil(centerIJK[1] + radiusVoxels + 1));
  const k0 = Math.max(0, Math.floor(centerIJK[2] - radiusVoxels - 1));
  const k1 = Math.min(dimK - 1, Math.ceil(centerIJK[2] + radiusVoxels + 1));
  const result: number[] = [];

  for (let k = k0; k <= k1; k += 1) {
    for (let j = j0; j <= j1; j += 1) {
      for (let i = i0; i <= i1; i += 1) {
        const world = ijkToWorld(origin, spacing, direction, i, j, k);
        const delta = subWorld(world, centerWorld);
        const normalDistance = Math.abs(dot3(normalWorld, delta));
        if (normalDistance > sliceHalfThickness) continue;

        const u = dot3(uWorld, delta);
        const v = dot3(vWorld, delta);
        if (u * u + v * v > radiusSquared) continue;

        result.push((k * dimJ + j) * dimI + i);
      }
    }
  }

  return result;
}

export function shouldRefreshPreviewForRangeChange(
  previousRange: readonly [number, number] | null | undefined,
  nextRange: readonly [number, number],
  hasCurrentPreview: boolean,
): boolean {
  if (!hasCurrentPreview || !previousRange) {
    return true;
  }

  return previousRange[0] !== nextRange[0] || previousRange[1] !== nextRange[1];
}
